/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useState, useEffect } from 'react';
import { styled, css, useTheme, SupersetTheme } from '@superset-ui/core';

import { Global } from '@emotion/react';
import { getUrlParam } from 'src/utils/urlUtils';
// import { Grid } from 'src/components';
import { MainNav as DropdownMenu } from 'src/components/Menu';
import { Tooltip } from 'src/components/Tooltip';
import { NavLink, useLocation } from 'react-router-dom';
import { GenericLink } from 'src/components/GenericLink/GenericLink';

import { useUiConfig } from 'src/components/UiConfigContext';
import { URL_PARAMS } from 'src/constants';
import {
  MenuObjectChildProps,
  MenuObjectProps,
  MenuData,
} from 'src/types/bootstrapTypes';

interface MenuProps {
  data: MenuData;
  isFrontendRoute?: (path?: string) => boolean;
}

const StyledHeader = styled.header`
  ${({ theme }) => `
      background-color: ${theme.colors.grayscale.light5};
      z-index: 10;
      height: 100vh;
      position: fixed;
      top: 0;
      left: 0;
      width: 240px;
      border-right: 1px solid ${theme.colors.grayscale.light2};
      display: flex;
      flex-direction: column;

      .caret {
        display: none;
      }
      .navbar-brand {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: ${theme.gridUnit * 3}px;
        border-bottom: 1px solid ${theme.colors.grayscale.light2};
        background-color: ${theme.colors.grayscale.light5};

        img {
          max-height: 40px;
          object-fit: contain;
        }
      }
      .navbar-brand-text {
        color: ${theme.colors.grayscale.dark1};
        padding: ${theme.gridUnit * 2}px 0;
        font-size: ${theme.gridUnit * 3}px;
        text-align: center;

        span {
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .main-nav {
        flex: 1;
        overflow-y: auto;
        border-right: none;
      }
      .main-nav .ant-menu-submenu-title > svg {
        display: none;
      }
      .ant-menu {
        background-color: transparent;
      }
      .ant-menu-item,
      .ant-menu-submenu {
        margin: 0;
      }
      .ant-menu-item,
      .ant-menu-submenu-title {
        height: 44px;
        line-height: 44px;
        padding: 0 ${theme.gridUnit * 4}px !important;
        margin: 0;
      }
      .ant-menu-item > a,
      .ant-menu-submenu-title {
        width: 100%;
      }
      .ant-menu-submenu-popup {
        left: 240px !important;
        top: 0 !important;
      }
      .ant-menu-vertical > .ant-menu-submenu {
        margin: 0;
      }
      .ant-menu-item a {
        &:hover {
          color: ${theme.colors.primary.base};
          background-color: ${theme.colors.primary.light5};
        }
      }
      @media (max-width: 767px) {
        width: 200px;
      }
  `}
`;
const globalStyles = (theme: SupersetTheme) => css`
  // body {
  //   margin-left: 240px;
  //   @media (max-width: 767px) {
  //     margin-left: 200px;
  //   }
  // }
  .ant-menu-submenu.ant-menu-submenu-popup.ant-menu.ant-menu-light {
    border-radius: 0px;
  }
  .ant-menu-vertical > .ant-menu-submenu.data-menu > .ant-menu-submenu-title {
    height: 28px;
    i {
      padding-right: ${theme.gridUnit * 2}px;
      margin-left: ${theme.gridUnit * 1.75}px;
    }
  }
  .ant-menu-item-selected {
    background-color: transparent;
    &:not(.ant-menu-item-active) {
      color: inherit;
      border-bottom-color: transparent;
      & > a {
        color: inherit;
      }
    }
  }
  .ant-menu-vertical > .ant-menu-item:has(> .is-active) {
    background-color: ${theme.colors.primary.light5};
    border-right: 3px solid ${theme.colors.primary.base};
    & > a {
      color: ${theme.colors.primary.base};
    }
  }
`;
const { SubMenu } = DropdownMenu;

// const { useBreakpoint } = Grid;

export function Menu({
  data: {
    menu,
    brand,
    navbar_right: navbarRight,
    settings,
    environment_tag: environmentTag,
  },
  isFrontendRoute = () => false,
}: MenuProps) {
  // const [showMenu, setMenu] = useState<MenuMode>('inline');
  // const screens = useBreakpoint();
  const uiConfig = useUiConfig();
  const theme = useTheme();

  enum paths {
    EXPLORE = '/explore',
    DASHBOARD = '/dashboard',
    CHART = '/chart',
    DATASETS = '/tablemodelview',
  }

  const defaultTabSelection: string[] = [];
  const [activeTabs, setActiveTabs] = useState(defaultTabSelection);
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    switch (true) {
      case path.startsWith(paths.DASHBOARD):
        setActiveTabs(['Dashboards']);
        break;
      case path.startsWith(paths.CHART) || path.startsWith(paths.EXPLORE):
        setActiveTabs(['Charts']);
        break;
      case path.startsWith(paths.DATASETS):
        setActiveTabs(['Datasets']);
        break;
      default:
        setActiveTabs(defaultTabSelection);
    }
  }, [location.pathname]);

  const standalone = getUrlParam(URL_PARAMS.standalone);
  if (standalone || uiConfig.hideNav) return <></>;

  const renderSubMenu = ({
    label,
    childs,
    url,
    index,
    isFrontendRoute,
  }: MenuObjectProps) => {
    if (url && isFrontendRoute) {
      return (
        <DropdownMenu.Item key={label} role="presentation">
          <NavLink role="button" to={url} activeClassName="is-active">
            {label}
          </NavLink>
        </DropdownMenu.Item>
      );
    }
    if (url) {
      return (
        <DropdownMenu.Item key={label}>
          <a href={url}>{label}</a>
        </DropdownMenu.Item>
      );
    }
    return (
      <SubMenu key={index} title={label}>
        {childs?.map((child: MenuObjectChildProps | string, index1: number) => {
          if (typeof child === 'string' && child === '-' && label !== 'Data') {
            return <DropdownMenu.Divider key={`$${index1}`} />;
          }
          if (typeof child !== 'string') {
            return (
              <DropdownMenu.Item key={`${child.label}`}>
                {child.isFrontendRoute ? (
                  <NavLink
                    to={child.url || ''}
                    exact
                    activeClassName="is-active"
                  >
                    {child.label}
                  </NavLink>
                ) : (
                  <a href={child.url}>{child.label}</a>
                )}
              </DropdownMenu.Item>
            );
          }
          return null;
        })}
      </SubMenu>
    );
  };
  return (
    <StyledHeader
      className="top"
      id="main-menu"
      role="navigation"
      style={{ display: 'none' }}
    >
      <Global styles={globalStyles(theme)} />
      <Tooltip
        id="brand-tooltip"
        placement="right"
        title={brand.tooltip}
        arrowPointAtCenter
      >
        {isFrontendRoute(window.location.pathname) ? (
          <GenericLink className="navbar-brand" to={brand.path}>
            <img src={brand.icon} alt={brand.alt} />
            {brand.text && (
              <div className="navbar-brand-text">
                <span>{brand.text}</span>
              </div>
            )}
          </GenericLink>
        ) : (
          <a className="navbar-brand" href={brand.path}>
            <img src={brand.icon} alt={brand.alt} />
            {brand.text && (
              <div className="navbar-brand-text">
                <span>{brand.text}</span>
              </div>
            )}
          </a>
        )}
      </Tooltip>
      <DropdownMenu
        mode="inline"
        data-test="navbar-top"
        className="main-nav"
        selectedKeys={activeTabs}
      >
        {menu.map((item, index) => {
          const props = {
            index,
            ...item,
            isFrontendRoute: isFrontendRoute(item.url),
            childs: item.childs?.map(c => {
              if (typeof c === 'string') {
                return c;
              }

              return {
                ...c,
                isFrontendRoute: isFrontendRoute(c.url),
              };
            }),
          };

          return renderSubMenu(props);
        })}
      </DropdownMenu>
    </StyledHeader>
  );
}

// transform the menu data to reorganize components
export default function MenuWrapper({ data, ...rest }: MenuProps) {
  const newMenuData = {
    ...data,
  };
  // Menu items that should go into settings dropdown
  const settingsMenus = {
    Data: true,
    Security: true,
    Manage: true,
  };

  // Cycle through menu.menu to build out cleanedMenu and settings
  const cleanedMenu: MenuObjectProps[] = [];
  const settings: MenuObjectProps[] = [];
  newMenuData.menu.forEach((item: any) => {
    if (!item) {
      return;
    }

    const children: (MenuObjectProps | string)[] = [];
    const newItem = {
      ...item,
    };

    // Filter childs
    if (item.childs) {
      item.childs.forEach((child: MenuObjectChildProps | string) => {
        if (typeof child === 'string') {
          children.push(child);
        } else if ((child as MenuObjectChildProps).label) {
          children.push(child);
        }
      });

      newItem.childs = children;
    }

    if (!settingsMenus.hasOwnProperty(item.name)) {
      cleanedMenu.push(newItem);
    } else {
      settings.push(newItem);
    }
  });

  newMenuData.menu = cleanedMenu;
  newMenuData.settings = settings;

  return <Menu data={newMenuData} {...rest} />;
}
